import { Save, Undo } from "@mui/icons-material";
import { Button, FormControl, Grid } from "@mui/material";
import * as React from "react";
import { useMutation } from "react-apollo";
import {
    ChannelSdiInput,
    ChannelStatusType,
    IngestProtocol,
    IngestType,
    TLAHardewareInterfaceGstDecklinkModesType,
    TLAHardewareInterfaceGstDecklinkProfileIdType,
    TLAHardewareInterfaceGstDecklinkVideoFormatType,
    TLAHardwareInterfaceDecklinkConnectionType,
} from "../../../__generated__/globalTypes";
import CardHESP from "../../CardHESP/CardHESP";
import CardHESPTitle from "../../CardHESP/CardHESPTitle";
import {
    ChangeIngestTypeMutation,
    ChangeIngestTypeMutationVariables,
} from "../../IngestServerSelector/__generated__/ChangeIngestTypeMutation";
import Loading from "../../Loading/Loading";
import { GET_CHANNEL_QUERY } from "../Overview/channelQueriesMutations";
import { GetChannelQuery_channel } from "../Overview/__generated__/GetChannelQuery";
import ChannelIngestSettingsPull from "./ChannelIngestSettingsPull";
import ChannelIngestSettingsPush from "./ChannelIngestSettingsPush";
import ChannelIngestSettingsSelector, { ProtocolToggle } from "./ChannelIngestSettingsSelector";
import gql from "graphql-tag";
import styled from "styled-components";
import HESPFormLabel from "../../HESPFormLabel/HESPFormLabel";
import { ChannelStreamConfigAudioInputWithId } from "./ChannelIngestAudioTracks";
import { v4 } from "uuid";
import ChannelSdiManagement from "../Create/ChannelSdiManagement";
import DropdownSelector from "../../Regions/DropdownSelector";
import _ from "lodash";
import ChannelCaptions, { ChannelStreamConfigCaptionInputWithId } from "./ChannelCaptions";

const DisabledIngestPullUrlForSDI = styled.input`
    font-size: 14px;
    border: 1px solid ${(props) => props.theme.lightGrayBottom};
    border-radius: 6px;
    padding: 6px 10px;
    outline: rgba(0, 0, 0, 0.6);
    color: #696969;
    width: 100%;
    cursor: not-allowed;
`;

interface Props {
    channel: GetChannelQuery_channel;
    disabled: boolean;
}

export const CHANGE_INGEST_TYPE_MUTATION = gql`
    mutation ChangeIngestTypeMutation($input: ChangeIngestTypeInput!) {
        changeIngestType(input: $input) {
            channelId
        }
    }
`;

function ChannelIngestSettings({ channel, disabled }: Props) {
    const { ingestType, channelId, ingestProtocol } = channel;

    const [selectedType, setSelectedType] = React.useState<IngestType>(ingestType);
    const [selectedProtocol, setSelectedProtocol] = React.useState<IngestProtocol>(ingestProtocol);

    const datacenterCanHandleSDI = channel.tla?.datacenter?.sdiInputs && channel.tla.datacenter.sdiInputs.length > 0;

    const [sdi, setSdi] = React.useState<ChannelSdiInput | undefined>(
        !datacenterCanHandleSDI
            ? undefined
            : channel.sdi ?? {
                  audioConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                  videoConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                  persistentId:
                      channel.tla?.datacenter?.sdiInputs && channel.tla?.datacenter?.sdiInputs.length > 0
                          ? channel.tla?.datacenter?.sdiInputs[0].persistentId
                          : "",
                  deviceNumber:
                      channel.tla?.datacenter?.sdiInputs && channel.tla?.datacenter?.sdiInputs.length > 0
                          ? channel.tla?.datacenter?.sdiInputs[0].deviceNumber
                          : 0,
                  profile: TLAHardewareInterfaceGstDecklinkProfileIdType.Default,
                  videoFormat: TLAHardewareInterfaceGstDecklinkVideoFormatType.Auto,
                  videoMode: TLAHardewareInterfaceGstDecklinkModesType.Auto,
              },
    );
    const [ingestUrl, setIngestUrl] = React.useState<string>(channel.ingestPullUrl ?? "");
    const [needsSave, setNeedsSave] = React.useState<boolean>(false);
    const [audioTracks, setAudioTracks] = React.useState<ChannelStreamConfigAudioInputWithId[]>(
        (channel.streamConfig.audio ?? []).map(
            (entry) =>
                ({
                    id: v4(),
                    label: entry.label,
                    lang: entry.lang,
                    pid: entry.pid,
                } as ChannelStreamConfigAudioInputWithId),
        ),
    );

    const [captions, setCaptions] = React.useState<ChannelStreamConfigCaptionInputWithId[]>(
        (channel.streamConfig.captions ?? []).map(
            (entry) =>
                ({
                    id: v4(),
                    label: entry.label,
                    language: entry.language,
                    channel: entry.channel,
                } as ChannelStreamConfigCaptionInputWithId),
        ),
    );

    React.useEffect(() => {
        setSelectedProtocol(channel.ingestProtocol);
        setSelectedType(channel.ingestType);
        setSdi(
            !datacenterCanHandleSDI
                ? undefined
                : channel.sdi ?? {
                      audioConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                      videoConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                      persistentId: channel.tla?.datacenter?.sdiInputs[0].persistentId ?? "",
                      deviceNumber: channel.tla?.datacenter?.sdiInputs[0].deviceNumber,
                      profile: TLAHardewareInterfaceGstDecklinkProfileIdType.Default,
                      videoFormat: TLAHardewareInterfaceGstDecklinkVideoFormatType.Auto,
                      videoMode: TLAHardewareInterfaceGstDecklinkModesType.Auto,
                  },
        );
        setIngestUrl(channel.ingestPullUrl ?? "");
    }, [channel, datacenterCanHandleSDI]);

    const [changeIngestTypeMut, { loading }] = useMutation<ChangeIngestTypeMutation, ChangeIngestTypeMutationVariables>(
        CHANGE_INGEST_TYPE_MUTATION,
    );

    function onSelectionChange(protocol: IngestProtocol, type: IngestType) {
        setSelectedType([IngestProtocol.srt, IngestProtocol.sdi].includes(protocol) ? IngestType.pull : type);
        setSelectedProtocol(protocol);
        setIngestUrl(protocol === channel.ingestProtocol ? channel.ingestPullUrl ?? "" : "");
        setNeedsSave(true);
    }

    function isOnPremEncoder(): boolean {
        // On-prem encoder is detected by TLA enabled + valid restream configuration
        return channel.tla?.enabled === true && (channel.streamConfig.restream?.length ?? 0) > 0;
    }

    function canShowRtsp() : boolean {
        return isOnPremEncoder();
    }

    function canShowNdi (): boolean {
        return isOnPremEncoder();
    }
    
    const sdiInputsForDC = channel.tla?.datacenter?.sdiInputs ?? [];
    const ndiSourcesForDC = channel.tla?.datacenter?.ndiSources ?? [];

    const protocolToggle: ProtocolToggle[] = [
        { protocol: IngestProtocol.rtmp, label: "RTMP", enabled: true },
        { protocol: IngestProtocol.srt, label: "SRT", enabled: true },
        { protocol: IngestProtocol.sdi, label: "SDI", enabled: sdiInputsForDC.length > 0 },
        { protocol: IngestProtocol.rtsp, label: "RTSP", enabled: canShowRtsp() },
        { protocol: IngestProtocol.ndi, label: "NDI", enabled: canShowNdi() },
    ];
    async function onSave () {
        if (isValid() && !disabled) {
            try {
                await changeIngestTypeMut({
                    variables: {
                        input: {
                            channelId,
                            organizationId: channel.organizationId,
                            ingestType: selectedType,
                            ingestProtocol: selectedProtocol,
                            ...(selectedType === IngestType.pull && {
                                ingestPullUrl: ingestUrl,
                                ...(selectedProtocol === IngestProtocol.srt && {
                                    audio: audioTracks.map((track) => ({
                                        label: track.label,
                                        lang: track.lang,
                                        pid: track.pid,
                                    })),
                                }),
                            }),
                            ...(selectedProtocol === IngestProtocol.sdi && {
                                sdi,
                            }),
                            captions: captions.map((caption) => ({
                                channel: caption.channel,
                                label: caption.label,
                                language: caption.language,
                            })),
                        },
                    },
                    refetchQueries: [
                        {
                            query: GET_CHANNEL_QUERY,
                            variables: {
                                channelId,
                                organizationId: channel.organizationId,
                            },
                        },
                    ],
                });
                setNeedsSave(false);
            } catch (_ignore: any) {}
        }
    }

    function validCaptions(): boolean {
        const allChannelsFilledPositive =
            captions.length === captions.filter((cap) => typeof cap.channel === "number" && cap.channel >= 0).length;
        const allLanguagesFilledIn =
            captions.length === captions.filter((cap) => cap.language.trim().length > 0).length;

        const allChannelsUnique =
            captions.map((cap) => cap.channel).length === _.uniq(captions.map((cap) => cap.channel)).length;
        const allLabelsUnique =
            captions.filter((cap) => cap.label.length > 0).map((cap) => cap.label).length ===
            _.uniq(captions.filter((cap) => cap.label.length > 0).map((cap) => cap.channel)).length;

        return allChannelsFilledPositive && allLanguagesFilledIn && allChannelsUnique && allLabelsUnique;
    }

    function isValid(): boolean {
        if (selectedType === IngestType.push) {
            return validCaptions();
        }

        if (selectedProtocol === IngestProtocol.rtmp) {
            if (!(ingestUrl.startsWith("rtmps://") || ingestUrl.startsWith("rtmp://"))) {
                return false;
            }
        }

        if (selectedProtocol === IngestProtocol.srt) {
            if (!ingestUrl.startsWith("srt://")) {
                return false;
            }
            if (audioTracks.length > 0) {
                if (audioTracks.length === 1) {
                    if (audioTracks[0].lang.trim().length === 0) {
                        return false;
                    }
                } else {
                    const errors = audioTracks.map((track) => {
                        return track.lang.trim().length === 0 || !track.pid || track.pid < 0;
                    });

                    if (errors.filter((error) => error === true).length > 0) {
                        return false;
                    }
                }
            }
        }

        if (!validCaptions()) {
            return false;
        }

        return true;
    }

    function onUndo(): void {
        setSelectedType(channel.ingestType);
        setIngestUrl(channel.ingestPullUrl ?? "");
        setSelectedProtocol(channel.ingestProtocol);
        setAudioTracks(
            (channel.streamConfig.audio ?? []).map(
                (entry) =>
                    ({
                        id: v4(),
                        label: entry.label,
                        lang: entry.lang,
                        pid: entry.pid,
                    } as ChannelStreamConfigAudioInputWithId),
            ),
        );
        setSdi(
            channel.sdi ?? {
                audioConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                videoConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                persistentId: channel.tla?.datacenter?.sdiInputs[0].persistentId ?? "",
                deviceNumber: channel.tla?.datacenter?.sdiInputs[0].deviceNumber,
                profile: TLAHardewareInterfaceGstDecklinkProfileIdType.Default,
                videoFormat: TLAHardewareInterfaceGstDecklinkVideoFormatType.Auto,
                videoMode: TLAHardewareInterfaceGstDecklinkModesType.Auto,
            },
        );
        setNeedsSave(false);
    }

    function constructSdiIngestUrl (): string {
        if (sdi) {
            const params = new URLSearchParams();

            const { ...items } = sdi;

            Object.keys(items).forEach((key) => {
                const kebabKey = _.kebabCase(key);
                const value = (sdi as any)[key];
                if (value !== undefined) {
                    params.append(kebabKey, value.toString());
                }
            });

            return `decklink://${sdi.deviceNumber ?? ""}?${params.toString()}`;
        }
        return "";
    }

    return (
        <>
            <CardHESP>
                <CardHESPTitle
                    title="Ingest server"
                    subtitle={disabled ? "Settings can only be updated when channel is stopped" : ""}
                    button={
                        loading ? (
                            <Loading />
                        ) : needsSave && !disabled ? (
                            <Grid container spacing={2}>
                                <Grid item>
                                    <Button
                                        startIcon={<Undo />}
                                        variant="contained"
                                        size="small"
                                        color="inherit"
                                        onClick={onUndo}
                                    >
                                        Undo
                                    </Button>
                                </Grid>{" "}
                                <Grid item>
                                    <Button
                                        startIcon={<Save />}
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        disabled={!isValid()}
                                        onClick={onSave}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        ) : null
                    }
                />
                <ChannelIngestSettingsSelector
                    disablePush={
                        selectedProtocol !== IngestProtocol.rtmp ||
                        (channel.tla?.enabled === true && channel.tla.datacenter?.rtmpPushEnabled !== true)
                    }
                    disabled={disabled}
                    selectedValues={{
                        protocol: selectedProtocol,
                        type: selectedType,
                    }}
                    onChange={(protocol: IngestProtocol, type: IngestType) => onSelectionChange(protocol, type)}
                    protocolToggle={protocolToggle.filter((it) => it.enabled)}
                />
                {selectedType === IngestType.push && <ChannelIngestSettingsPush channel={channel} />}
                {selectedType === IngestType.pull &&
                    selectedProtocol !== IngestProtocol.sdi &&
                    !(selectedProtocol === IngestProtocol.ndi && ndiSourcesForDC.length > 0) && (
                    <ChannelIngestSettingsPull
                        disabled={disabled}
                        ingestProtocol={selectedProtocol}
                        ingestUrl={ingestUrl}
                        onChangeIngestUrl={(url: string) => {
                            setIngestUrl(url);
                            setNeedsSave(true);
                        }}
                        audioTracks={audioTracks}
                        onChangeAudioTracks={(tracks: ChannelStreamConfigAudioInputWithId[]) => {
                            setAudioTracks(tracks);
                            setNeedsSave(true);
                        }}
                    />
                )}
                {selectedType === IngestType.pull &&
                    selectedProtocol === IngestProtocol.ndi &&
                    ndiSourcesForDC.length > 0 && (
                        <FormControl fullWidth>
                            <HESPFormLabel label="NDI source" size="small" />
                            <DropdownSelector
                                size="small"
                                selectedKey={ingestUrl}
                                onChangeSelected={(url: string) => {
                                    setIngestUrl(url);
                                    setNeedsSave(true);
                                }}
                                items={ndiSourcesForDC.map((source) => ({
                                    key: `ndi://q?name=${encodeURIComponent(source)}`,
                                    value: source,
                                }))}
                            />
                        </FormControl>
                    )}
                {selectedType === IngestType.pull &&
                    selectedProtocol === IngestProtocol.sdi &&
                    datacenterCanHandleSDI && (
                        <>
                            <FormControl fullWidth>
                                <HESPFormLabel label="SDI input" size="small" />
                                <DropdownSelector
                                    size="small"
                                    selectedKey={sdi?.persistentId ?? ""}
                                    onChangeSelected={(persistentId: string) => {
                                        setSdi({
                                            ...sdi!,
                                            persistentId,
                                            deviceNumber:
                                                channel.tla?.datacenter?.sdiInputs.find(
                                                    (dc) => dc.persistentId === persistentId,
                                                )?.deviceNumber ?? 0,
                                        });
                                        setNeedsSave(true);
                                    }}
                                    items={sdiInputsForDC.map((sdiInput) => ({
                                        key: sdiInput.persistentId,
                                        value: `${sdiInput.name} (${sdiInput.persistentId})`,
                                    }))}
                                />
                            </FormControl>
                            <div style={{ marginTop: "4px", marginBottom: "4px" }}>
                                <ChannelSdiManagement
                                    size="small"
                                    values={{
                                        audioConnection: sdi!.audioConnection,
                                        profile: sdi!.profile,
                                        videoConnection: sdi!.videoConnection,
                                        videoFormat: sdi!.videoFormat,
                                        videoMode: sdi!.videoMode,
                                    }}
                                    onChangeValues={(values) => {
                                        setSdi({ ...sdi!, ...values });
                                        setNeedsSave(true);
                                    }}
                                />
                            </div>
                            <label style={{ fontSize: "13px", fontWeight: "bolder", width: "80px" }}>URL:</label>
                            <DisabledIngestPullUrlForSDI value={constructSdiIngestUrl()} />
                        </>
                    )}
                {channel.tla?.enabled === true && (
                    <Grid item xs={12} style={{ marginTop: "8px" }}>
                        <ChannelCaptions
                            captions={captions}
                            onChangeCaptions={(capt) => {
                                setCaptions(capt);
                                setNeedsSave(true);
                            }}
                            disabled={channel.channelStatus !== ChannelStatusType.stopped}
                        />
                    </Grid>
                )}
            </CardHESP>
        </>
    );
}

export default ChannelIngestSettings;
